import React, {
    useEffect,
    useState
} from 'react';
import {
    Link,
    useParams,
    Navigate
} from 'react-router-dom';
import getImageUrl from '../../utils/getImageUrl'
import './portfolioDetail.css';
import API_BASE_URL from '../../config/api';

const PortfolioDetail = () => {
    const { id } = useParams();

    const [portfolio, setPortfolio] =
        useState(null);

    const [imageIndex, setImageIndex] =
        useState(0);

    const [isLoading, setIsLoading] =
        useState(true);

    const [notFound, setNotFound] =
        useState(false);

    const [error, setError] =
        useState('');

    useEffect(() => {
        const fetchPortfolio = async () => {
            try {
                setIsLoading(true);
                setNotFound(false);
                setError('');

                const response = await fetch(
                    `${API_BASE_URL}/api/portfolios/public/${id}`
                );

                if (response.status === 404) {
                    setNotFound(true);
                    return;
                }

                const result =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        '시공사례를 불러오지 못했습니다.'
                    );
                }

                setPortfolio(
                    result.portfolio
                );
            } catch (error) {
                console.error(
                    '시공사례 상세 조회 오류:',
                    error
                );

                setError(
                    error.message
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchPortfolio();
    }, [id]);

    useEffect(() => {
        setImageIndex(0);
    }, [id]);

    if (notFound) {
        return (
            <Navigate
                to="/portfolio"
                replace
            />
        );
    }

    if (isLoading) {
        return (
            <div className="portfolio-detail">
                <section className="portfolio-detail-content">
                    <div className="portfolio-detail-container">
                        <div className="portfolio-detail-loading">
                            <i className="mdi mdi-loading mdi-spin"></i>

                            <strong>
                                시공사례를 불러오는 중입니다.
                            </strong>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (
        error ||
        !portfolio
    ) {
        return (
            <div className="portfolio-detail">
                <section className="portfolio-detail-content">
                    <div className="portfolio-detail-container">
                        <div className="portfolio-detail-loading">
                            <i className="mdi mdi-alert-circle-outline"></i>

                            <strong>
                                시공사례를 불러오지 못했습니다.
                            </strong>

                            <p>
                                {error}
                            </p>

                            <Link to="/portfolio">
                                시공사례 목록으로
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    const images = [
        portfolio.thumbnail_image,
        ...(portfolio.detail_images || []).map(
            (image) => image.image_url
        )
    ]
        .filter(Boolean)
        .map(
            (image) =>
                getImageUrl(image)
        );

    const selectedImage =
        images[imageIndex] ||
        images[0];

    const workDate =
        portfolio.work_date
            ? portfolio.work_date.substring(
                0,
                10
            )
            : '-';

    return (
        <div className="portfolio-detail">

            {/* SUB VISUAL */}
            <section className="portfolio-detail-sub">
                <div className="portfolio-detail-sub-overlay"></div>

                <div className="portfolio-detail-sub-inner">
                    <h1>
                        시공사례
                    </h1>

                    <div>
                        <Link to="/">
                            HOME
                        </Link>

                        <span>
                            ›
                        </span>

                        <Link to="/portfolio">
                            시공사례
                        </Link>

                        <span>
                            ›
                        </span>

                        <strong>
                            {portfolio.title}
                        </strong>
                    </div>
                </div>
            </section>

            {/* DETAIL */}
            <section className="portfolio-detail-content">
                <div className="portfolio-detail-container">

                    <h2 className="portfolio-detail-title">
                        {portfolio.title}
                    </h2>

                    <div className="portfolio-detail-top">

                        {/* IMAGE */}
                        <div className="portfolio-detail-gallery">

                            {selectedImage && (
                                <div className="portfolio-detail-main-image">
                                    <img
                                        src={selectedImage}
                                        alt={portfolio.title}
                                    />
                                </div>
                            )}

                            {images.length > 1 && (
                                <div className="portfolio-detail-thumbnails">

                                    {images.map(
                                        (image, index) => (
                                            <button
                                                type="button"
                                                key={`${image}-${index}`}
                                                className={
                                                    imageIndex === index
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    setImageIndex(index)
                                                }
                                            >
                                                <img
                                                    src={image}
                                                    alt={`${portfolio.title} ${index + 1}`}
                                                />
                                            </button>
                                        )
                                    )}

                                </div>
                            )}

                        </div>

                        {/* INFORMATION */}
                        <div className="portfolio-detail-info">

                            <InfoRow
                                label="설치유형"
                                value={
                                    portfolio.category ||
                                    '-'
                                }
                            />

                            <InfoRow
                                label="업종"
                                value={
                                    portfolio.industry ||
                                    '-'
                                }
                            />

                            <InfoRow
                                label="장소"
                                value={
                                    portfolio.location ||
                                    '-'
                                }
                            />

                            <InfoRow
                                label="설치일"
                                value={workDate}
                            />

                            <InfoRow
                                label="설치제품"
                                value={
                                    portfolio.product ||
                                    '-'
                                }
                            />

                            <InfoRow
                                label="시공사"
                                value={
                                    portfolio.contractor ||
                                    '-'
                                }
                            />

                            <div className="portfolio-info-description">
                                <strong>
                                    시공내용
                                </strong>

                                <p>
                                    {portfolio.description ||
                                        '등록된 시공 내용이 없습니다.'
                                    }
                                </p>
                            </div>

                        </div>

                    </div>

                    {/* DETAIL IMAGES */}
                    {portfolio.detail_images &&
                        portfolio.detail_images.length > 0 && (
                            <div className="portfolio-process">

                                <div className="portfolio-process-heading">
                                    <p>
                                        INSTALLATION GALLERY
                                    </p>

                                    <h2>
                                        시공 현장
                                    </h2>
                                </div>

                                <div className="portfolio-process-grid">

                                    {portfolio.detail_images.map(
                                        (image, index) => (
                                            <div
                                                className="portfolio-process-item"
                                                key={
                                                    image.portfolio_image_id
                                                }
                                            >
                                                <div>
                                                    <img
                                                        src={getImageUrl(
                                                            image.image_url
                                                        )}
                                                        alt={
                                                            `${portfolio.title} 상세 이미지 ${index + 1}`
                                                        }
                                                    />
                                                </div>

                                                <strong>
                                                    현장 이미지 {index + 1}
                                                </strong>
                                            </div>
                                        )
                                    )}

                                </div>

                            </div>
                        )}

                    {/* BOTTOM */}
                    <div className="portfolio-detail-bottom">

                        <div>
                            <i className="mdi mdi-view-grid-outline"></i>

                            <div>
                                <strong>
                                    다른 시공사례도 확인해보세요.
                                </strong>

                                <p>
                                    다양한 환경의 시공사례를
                                    확인하실 수 있습니다.
                                </p>
                            </div>
                        </div>

                        <Link to="/portfolio">
                            목록으로 돌아가기

                            <i className="mdi mdi-arrow-right"></i>
                        </Link>

                    </div>

                </div>
            </section>

        </div>
    );
};

const InfoRow = ({
    label,
    value
}) => {
    return (
        <div className="portfolio-info-row">
            <strong>
                {label}
            </strong>

            <span>
                {value}
            </span>
        </div>
    );
};

export default PortfolioDetail;